import {
  clearCartCache,
  clearSavedForLaterCache,
  getCurrentOrder,
  getSavedForLaterItems,
  LineItem,
  LineItemOp,
  update
} from '@/api/mercury/cart'
import {
  AvettiCartItem,
  clearCartCache as avettiClearCartCache,
  getAvettiCart,
  getAvettiSavedForLater,
  refreshCart,
  update as avettiUpdate
} from './avetti/cart'
import { useMercury } from './mercury/utils'
import {
  CartLineItem,
  getCartAndSavedForLaterTreesWithProofData
} from '@/services/cart'
import { roundCurrency } from '@/utils/number'
import { NativeFulfillmentAttributes, proofData } from '@/api/commerce/proof'
import { itemsJson } from '@/services/item'
import { Item } from '@/api/items'

/**
 * Sets a `parentUuid` for each item in the array based on proof data.
 * The `parentUuid` is derived from the project ID if it exists and does not match the item's `uuid`.
 *
 * @returns A new array of items with `parentUuid` added for each item.
 */
export const withParentUuid = async <T>(
  cartLineItems: (T & { uuid: string })[]
): Promise<(T & { parentUuid: string | null })[]> => {
  const proofs = await proofData(...cartLineItems.map((item) => item.uuid))

  return cartLineItems.map((item) => {
    const proof = proofs[item.uuid]
    const projectId = proof?.projectId
    const parentUuid = projectId && projectId !== item.uuid ? projectId : null
    return { ...item, parentUuid }
  })
}

/**
 * Sets a `quantitySyncMultiplier` for each item in the array based on proof and item data.
 * Used to augment Avetti cart data to add `quantitySyncMultiplier` and simulate that the cart
 * returns it.
 *
 * @returns Cart line items with `quantitySyncMultiplier` overwritten with
 * data from fulfillment attributes.
 */
export const setQuantitySyncMultiplierFromFulfillmentAttributes = async (
  lineItems: CartLineItem[]
): Promise<CartLineItem[]> =>
  Promise.all(
    lineItems.map(async (lineItem) => {
      // Fulfillment attributes with quantity sync multiplier data are stored with the parent proof
      if (lineItem.parentUuid) {
        const itemProofData = (await itemsJson(lineItem.itemId))[
          lineItem.itemId
        ]!
        if (
          itemProofData.quantitySyncBehavior ===
            'MAIN_MULTIPLIED_BY_FULFILLMENT_ATTRIBUTE' &&
          itemProofData.quantitySyncFulfillmentAttribute !== null
        ) {
          // Network requests could be consolidated with a bulk request,
          // but a minority of items will get to this point.
          // Individual proof data requests enables pipelining item and proof data requests
          // to avoid waiting for all item data to be fetched before getting proof data.
          const parentProofData = (await proofData(lineItem.parentUuid))[
            lineItem.parentUuid
          ]
          if (parentProofData) {
            const quantitySyncFulfillmentAttribute =
              parentProofData.attributes.fulfillment[
                itemProofData.quantitySyncFulfillmentAttribute
              ]

            // Should never be 0 since the add-on would be deleted if there was 0
            if (quantitySyncFulfillmentAttribute) {
              return {
                ...lineItem,
                quantitySyncMultiplier: quantitySyncFulfillmentAttribute
              }
            }
          }
        }
      }
      return {
        ...lineItem,
        quantitySyncMultiplier: null
      }
    })
  )

/**
 * Interface which describes how should the operations looks from the service, to hide api details
 */
export interface LineItemOpRequest extends Omit<LineItemOp, 'variantId'> {
  itemId?: number
  children?: LineItemOpRequest[]
}

/**
 * Un-nests `children` and returns a single flat array of items.
 *
 * There is no effort made to clear the `children`, or set the `parentUuid` of un-nested items.
 * This will be used to adapt requests to the Avetti API, which doesn't support either of these fields.
 */
export const flattenLineItemOps = (
  ops: LineItemOpRequest[]
): LineItemOpRequest[] =>
  ops.reduce<LineItemOpRequest[]>(
    (acc, item) => [...acc, item, ...flattenLineItemOps(item.children ?? [])],
    []
  )

/**
 * Destroys existing immediate children, if they aren't present in the current ops' `children`,
 * for ops with `_destroyExtraChildren: true`.
 */
const destroyExtraChildrenOps = async (
  lineItemsOps: LineItemOpRequest[]
): Promise<LineItemOpRequest[]> => {
  const { cart, savedForLater } =
    await getCartAndSavedForLaterTreesWithProofData()

  const cartPlusSaved = [...cart, ...savedForLater]

  return lineItemsOps.reduce((destroyOps, parentItem) => {
    if (parentItem._destroyExtraChildren) {
      const parentInCart = cartPlusSaved.find(
        (item) => item.uuid === parentItem.uuid
      )

      if (parentInCart) {
        const childrenToDelete = parentInCart.children
          .filter(
            (child) =>
              !parentItem.children?.some(
                (opChild) => child.uuid === opChild.uuid
              )
          )
          .map((child) => ({
            uuid: child.uuid,
            _destroy: true
          }))
        return [...destroyOps, ...childrenToDelete]
      }
    }
    return destroyOps
  }, [] as LineItemOpRequest[])
}

/**
 * Modify the operations to match with mercury ops interface.
 */
export const updateAdapter = async (
  lineItemOps: LineItemOpRequest[]
): Promise<void> => {
  if (useMercury()) {
    await update(
      lineItemOps.map((operation) => {
        const { itemId: variantId, ...operationWithoutItemId } = operation
        return { variantId, ...operationWithoutItemId }
      })
    )
  } else {
    const flattenedLineItemOps = flattenLineItemOps(lineItemOps)
    if (flattenedLineItemOps.some((op) => op.lineItemId)) {
      throw new Error('Avetti does not support lineItemId')
    }
    if (
      flattenedLineItemOps.some(
        (op) => op.customizationId && op.customizationId !== op.uuid
      )
    ) {
      throw new Error(
        'Avetti does not support a customizationId that does not match the uuid'
      )
    }
    if (
      // Prevent trying to increment the quantity of a personalized product.
      // Avetti uses the item type in the product properties to determine whether to increment
      // the quantity (static items) or set it absolutely (personalized items).
      // This validation is approximate and assumes that if a customizationId is provided,
      // this operation is for a personalized item even though technically that may not be the case.
      // This will validate that _incrementQuantity is never true for personalized items.
      flattenedLineItemOps.some(
        (op) => op._incrementQuantity && op.customizationId
      )
    ) {
      throw new Error(
        'Avetti will always set an absolute quantity on personalized items'
      )
    }

    await avettiUpdate([
      ...(await destroyExtraChildrenOps(lineItemOps)),
      ...flattenedLineItemOps.map((lineItemOp) => ({
        uuid: lineItemOp.uuid,
        _destroy: lineItemOp._destroy,
        savedForLater: lineItemOp.savedForLater,
        quantity: lineItemOp.quantity,
        itemId: lineItemOp.itemId
      }))
    ])
  }
}

/**
 * convert avetti quantity list to better data structure
 */
function qtyListToCartLineItemQuantityData(qtyList: {
  [key: string]: string
}): {
  minQuantity: number
  maxQuantity: number
  quantityIncrement: number
} {
  if (typeof qtyList === 'object') {
    const qtyListNumbers: number[] = Object.values(qtyList).map(Number)
    return {
      // Backend qty list is guaranteed to have at least 2 entries (we ignore the last one)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      minQuantity: qtyListNumbers[0]!,
      // Last value is always irrelevant due to auto-appended "Call for #+"
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      maxQuantity: qtyListNumbers[qtyListNumbers.length - 2]!,
      quantityIncrement: qtyListNumbers[1]
        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          qtyListNumbers[1] - qtyListNumbers[0]!
        : 1
    }
  } else {
    // `qtyList` isn't present for saved for later items in Avetti.
    // Fall back to dummy values using `Item.Defaults` to ensure all items have the data available,
    // but we can assume these values won't be used.
    // In the future, Mercury will always send the relevant data for all items.
    return {
      minQuantity: 1,
      maxQuantity: 100,
      quantityIncrement: 1
    }
  }
}

/**
 * This function is used as a helper to transform Avetti cart response to common CartLineItem format
 */
export const avettiCartResponseToCartLineItem = async (
  cartItemList: AvettiCartItem[],
  isSavedForLater: boolean
): Promise<CartLineItem[]> =>
  await withParentUuid(
    cartItemList.map((item: AvettiCartItem) => {
      const quantityShippedWithoutRecipients = item.quantityShipped || 0
      // Avetti saved for later doesn't have `quantityMailed`, so this will always be 0 for those items
      const quantityRecipients = item.quantityMailed || 0
      // Avetti saved for later doesn't have pricing information, but we don't actually use this in the UI
      const total = item.totalPrice || 0
      // TODO: Once address book is extracted from Avetti, this may not be needed because all items may have recipients
      // addressListUsed may be true even for items saved for later, but this is an invalid
      // state, which Avetti may also change to false after an item in saved for later is updated.
      // Therefore, don't allow this to be true in the CartLineItem, regardless of the API response.
      const isAddressListUsed = !isSavedForLater && item.addressListUsed

      return {
        uuid: item.proofId,
        // TODO: Get proof data in the adapter to add this for Avetti.
        //   We don't need this in the service/adapter until we send children to Mercury later on.
        // parentUuid: 'string | null',
        customizationId: item.proofId,
        itemId: item.itemId,
        // TODO: Remove quantityRecipients and quantityShippedWithoutRecipients once recipients are extracted from Avetti
        quantityRecipients,
        quantityShippedWithoutRecipients,
        quantity: quantityShippedWithoutRecipients + quantityRecipients,
        recipientDeliveryMethod: isAddressListUsed
          ? item.addressListShipped
            ? 'SHIPPED'
            : 'MAILED'
          : null,
        price: roundCurrency(
          total / (quantityShippedWithoutRecipients + quantityRecipients)
        ),
        total,
        projectedMailDate: item.mailDate,
        // TODO: Inject scheduledShipDate from fulfillment attributes later.
        scheduledShipDate: null,

        // TODO: Remove projectedShipDate once mailed and addresses is decoupled from Avetti
        projectedShipDate: item.shipDate,
        ...qtyListToCartLineItemQuantityData(item.qtyList),
        shortTitle: item.title,
        hugeImageUrl: item.catalogImage,
        code: item.compCode,
        created: item.created,
        cartBehavior: item.itemType,
        savedForLater: isSavedForLater,
        formatName: item.formatName,
        // Unlike Mercury, Avetti doesn't persist this.
        // With API calls for item and proof data, it can be injected via data from fulfillment attributes.
        // However, since it's only used for quantity sync calculations,
        // it will only be injected during updates (in the cart service) to optimize read-only performance.
        quantitySyncMultiplier: null
      }
    })
  )

/**
 * This function is used as a helper to transform Mercury cart response to common CartLineItem format
 */
export const mercuryCartResponseToCartLineItem = (
  cartItemList: LineItem[],
  isSavedForLater: boolean
): CartLineItem[] =>
  cartItemList.map(
    (item: LineItem): CartLineItem => ({
      uuid: item.uuid,
      customizationId: item.customizationId,
      itemId: item.variantId,
      quantityRecipients: 0,
      quantityShippedWithoutRecipients: item.quantity,
      quantity: item.quantity,
      recipientDeliveryMethod: null,
      price: item.price,
      total: item.total,
      // Mercury won't treat mailed items differently from shipped ones and won't be aware of recipients
      projectedMailDate: null,
      scheduledShipDate: item.scheduledShipDate,
      projectedShipDate: item.projectedShipDate,
      minQuantity: item.variant.minQuantity,
      maxQuantity: item.variant.maxQuantity,
      quantityIncrement: item.variant.quantityIncrement,
      shortTitle: item.variant.name,
      hugeImageUrl: item.variant.images[0]?.largeUrl || null,
      code: item.variant.sku,
      created: item.createdAt,
      cartBehavior: item.variant.cartBehavior,
      savedForLater: isSavedForLater,
      formatName: item.variant.formatName,
      parentUuid: item.parentUuid,
      quantitySyncMultiplier: item.quantitySyncMultiplier
    })
  )

/**
 * This function is used as a helper to transform Avetti saved for later response to common CartLineItem format
 */
export async function getSavedForLaterAdapter(): Promise<
  CartLineItem[] | null
> {
  if (useMercury()) {
    return mercuryCartResponseToCartLineItem(
      (await getSavedForLaterItems()) || [],
      true
    )
  } else {
    return await avettiCartResponseToCartLineItem(
      await getAvettiSavedForLater(),
      true
    )
  }
}

/**
 * This function is used as a helper to transform Avetti cart response to common CartLineItem format
 */
export async function getCurrentOrderAdapter(): Promise<CartLineItem[] | null> {
  if (useMercury()) {
    return mercuryCartResponseToCartLineItem(
      (await getCurrentOrder()) || [],
      false
    )
  } else {
    return await avettiCartResponseToCartLineItem(await getAvettiCart(), false)
  }
}

// TODO: Remove any refresh cart code once the address book is decoupled from Avetti (i.e. before Mercury launch).
export const refreshCartAdapter = refreshCart

export const clearCartCacheAdapter = useMercury()
  ? clearCartCache
  : avettiClearCartCache

export const clearSavedForLaterCacheAdapter = useMercury()
  ? clearSavedForLaterCache
  : avettiClearCartCache

/**
 * Returns `quantitySyncMultiplier` for an upsert line item op based on the add-on item and fulfillment attributes
 * returned from the proof API.
 *
 * This is only for extra pages as of 7/2024.
 *
 * A non-null value is returned for line item op whose item's `quantitySyncBehavior`
 * is `MAIN_MULTIPLIED_BY_FULFILLMENT_ATTRIBUTE` if the fulfillment attributes of the add-on's parent
 * (the proof attributes of the photo book), has a value set for the key defined by the add-on item's
 * `quantitySyncFulfillmentAttribute`.
 *
 * @param item Partial item data with `quantitySyncBehavior` and `quantitySyncFulfillmentAttribute` defined.
 * @param fulfillmentAttributes Fulfillment attributes for the photo book, which may define `extraPageCount`.
 */
export const getAddOnQuantitySyncMultiplier = (
  item: Pick<Item, 'quantitySyncFulfillmentAttribute' | 'quantitySyncBehavior'>,
  fulfillmentAttributes: NativeFulfillmentAttributes
): number | null => {
  if (
    item.quantitySyncFulfillmentAttribute === null ||
    item.quantitySyncBehavior !== 'MAIN_MULTIPLIED_BY_FULFILLMENT_ATTRIBUTE'
  ) {
    return null
  }

  const quantitySyncMultiplier =
    fulfillmentAttributes[item.quantitySyncFulfillmentAttribute]
  return quantitySyncMultiplier ?? null
}
